import React, { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import moment, { Moment } from 'moment'
import { isEmpty, pickBy } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import uuid from 'react-uuid'
import {
  UserTask,
  UserTaskSource,
  useCreateUserTaskMutation,
  useSearchHowToDoTaskMutation,
  useUpdateUserTaskMutation,
  useGetUserTaskQuery,
  BusinessGoalPriority,
  UserTaskItemStatus,
  useUpdateBusinessGoalMutation,
  UserTaskSuggestedItemStatus,
  usePopulateTaskInformationMutation,
  useGetFilteredBusinessGoalsQuery,
  UserTaskHowToStep,
  TutorialGenerationStatus,
} from '__generated__/api-types-and-hooks'
import hookForms from 'utils/hookForms'
import { FORM, GOALIDS } from 'appConfig/enums'
import { formKeys, otherGoalIds } from 'appConfig/data'
import { getTenantId } from 'utils/getTenantId'
import { useParams } from 'react-router-dom'
import useFormContainer from 'hooks/useFormContainer'
import { setFormData } from 'store/actions/form'
import { playCreationForm } from 'appUtils/appValidation'
import {
  calculateNewGoalEndDate,
  calculateTotalEstimationHoursOfUserTasks,
  calculateWorkingDaysBetweenTwoDates,
  isDateInCurrentWeekAndPast,
} from 'appUtils'
import { RootState } from 'App'
import { TaskFormFields } from 'components/AppKanbanBoard/config'

interface IUseUserTaskProps {
  task?: UserTask
  plan?: {
    name: string | null | undefined
    id: string | null | undefined
    planExpectedEndDate: string | null | undefined
    startDate?: string
  }
  hoursSpentPerDay: number
  tenantId: string
  userId: string
  meetingId?: string
  allTasks?: UserTask[]
  goalId?: string
  goalName?: string
  advisorsData: Array<{ label: any; value: any }>
  source: UserTaskSource
  setShowTaskModal: React.Dispatch<React.SetStateAction<boolean>>
  setShowTaskSuccessModal?: React.Dispatch<React.SetStateAction<boolean>>
  setShowTaskErrorModal?: React.Dispatch<React.SetStateAction<boolean>>
  setCreatedUserTask?: React.Dispatch<React.SetStateAction<UserTask>>
  refetch: () => void
  goalEndDate: string
  initialDescription?: string // New prop for chat context
  paramUserId?: string
}

export const useUserTask = ({
  task,
  plan,
  goalEndDate,
  hoursSpentPerDay,
  tenantId,
  userId,
  meetingId,
  allTasks,
  goalId,
  goalName,
  advisorsData,
  source,
  setShowTaskModal,
  setShowTaskSuccessModal,
  setShowTaskErrorModal,
  setCreatedUserTask,
  refetch,
  initialDescription, // Extract the initial description prop
  paramUserId,
}: IUseUserTaskProps) => {
  const queryClient = useQueryClient()
  const isViewMode = Boolean(task)
  const user = useSelector((state: RootState) => state.user.user)
  const { clientId } = useParams()

  // Get available goals to determine if we should default to MY_OTHER_TASKS
  const { data: goalsData, isLoading: isGoalsLoading } = useGetFilteredBusinessGoalsQuery(
    {
      input: { userId: paramUserId || clientId, tenantId: getTenantId() },
    },
    { refetchOnWindowFocus: false }
  )

  // Check if there are active business goals available (excluding archived, paused, or completed goals)
  const hasOtherGoalsAvailable =
    goalsData?.getBusinessGoals?.some(
      (goal) =>
        goal.status !== UserTaskItemStatus.Archived &&
        goal.status !== UserTaskItemStatus.Paused &&
        goal.status !== UserTaskItemStatus.Done
    ) ?? false

  // Only determine the default goal after goals have loaded
  const getDefaultGoalId = () => {
    if (isGoalsLoading) return undefined
    if (!isAllTasks && goalId) return goalId
    return hasOtherGoalsAvailable ? undefined : GOALIDS.MY_OTHER_TASKS
  }

  const [generatedHowToDo, setGeneratedHowToDo] = useState('')
  const [tutorialNotFound, setTutorialNotFound] = useState(false)
  const [structuredSteps, setStructuredSteps] = useState<UserTaskHowToStep[] | undefined>(undefined)
  const [isTutorialGenerating, setIsTutorialGenerating] = useState(false)
  const [tutorialGenerationStartTime, setTutorialGenerationStartTime] = useState<number | null>(
    null
  )
  const [showWarningPopup, setShowWarningPopup] = useState<
    | 'effortPopup'
    | 'moreThanCapacityPopup'
    | 'startDateOutsideGoalTimelinePopup'
    | 'endDateOutsideGoalTimelinePopup'
    | 'none'
  >('none')
  const [goalExpectedEndDate, setGoalExpectedEndDate] = useState<string>(goalEndDate ?? '')
  const planStartDate = useRef(plan?.startDate)
  const [, setTaskStartDate] = useState<string>(task?.startDate ?? moment().format('YYYY-MM-DD'))

  const [clickedFields, setClickedFields] = useState<{ [key: string]: boolean }>({
    taskPriority: false,
    status: false,
    milestoneId: false,
    clickedFieldsategory: false,
    startDate: false,
    expectedEndDate: false,
    estimatedEffortInMinutes: false,
    assigneeId: false,
    name: false,
    instructionForTask: false,
    goalId: false,
  })
  const reactQueryClient = useQueryClient()
  const [isStartDateBeforePlanStartDate, setIsStartDateBeforePlanStartDate] = useState(false)
  const isAllTasks = otherGoalIds.includes(goalId as GOALIDS)

  // Both create and edit use playCreationForm so an existing past start date
  // doesn't block edits to other fields like due date. Ordering (start <= due)
  // is still enforced via mandatoryStartDate/mandatoryExpectedEndDate.
  const taskFormSchema = playCreationForm
  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(taskFormSchema),
  })
  const {
    setValue,
    clearErrors,
    formState: { errors },
  } = methods
  useFormContainer(FORM.USER_TASK_FORM, methods)
  const {
    mutate: createUserTask,
    isLoading: isCreateTaskLoading,
    isSuccess: isCreateTaskSuccess,
    isError: isCreateTaskError,
  } = useCreateUserTaskMutation()

  const {
    mutate: updateUserTask,
    isSuccess: isUpdateTaskSucess,
    isLoading: updateTaskLoading,
  } = useUpdateUserTaskMutation()
  const { mutate: updateGoal } = useUpdateBusinessGoalMutation()
  const { mutate: populateTaskInformation } = usePopulateTaskInformationMutation()
  // console.log({ updateTaskLoading })
  const shouldShowWarningPopup = (
    allTasks: UserTask[],
    expectedDate: Moment,
    goalExpectedEndDate: Moment,
    currentTaskEstimatedHours: number
  ): boolean => {
    const workingDays = calculateWorkingDaysBetweenTwoDates(expectedDate, goalExpectedEndDate)
    const tasksHoursAfterExpextedDate = calculateTotalEstimationHoursOfUserTasks(
      allTasks,
      expectedDate,
      task?.taskId ?? ''
    )
    const availableHours = workingDays * hoursSpentPerDay
    // Calculate hours difference once and use it for setting days increased and condition checks
    const freeHours = availableHours - tasksHoursAfterExpextedDate
    // Show warning if available hours are less than total task hours or if the task estimation exceeds remaining hours
    const showWarning =
      availableHours < tasksHoursAfterExpextedDate || currentTaskEstimatedHours > freeHours
    if (showWarning) {
      // Calculate additional days needed to cover the shortfall
      let hoursToBeAdded = currentTaskEstimatedHours - freeHours
      if (expectedDate.isAfter(goalExpectedEndDate, 'days')) {
        const numberOfDaysToBeIncreased =
          calculateWorkingDaysBetweenTwoDates(goalExpectedEndDate, expectedDate) - 2
        hoursToBeAdded += numberOfDaysToBeIncreased * hoursSpentPerDay
      }
      const newEndDate = calculateNewGoalEndDate(
        goalExpectedEndDate,
        hoursToBeAdded,
        hoursSpentPerDay
      )
      setGoalExpectedEndDate(newEndDate)
    }
    return showWarning
  }

  const handleClick = async (field: string, isHovered: boolean) => {
    setClickedFields((prev) => ({ ...prev, [field]: isHovered }))
    if (isHovered) return
    const data = hookForms.getForm(FORM.USER_TASK_FORM)?.getValues()
    const startDate = moment(data.startDate)
    let isAssigneeChangedForTask = false

    // Cross-field validation: changing either date must re-validate both so a
    // stale "Start date cannot be after due date" error clears when the user
    // fixes ordering by editing the other field.
    if (field === TaskFormFields.startDate || field === TaskFormFields.expectedEndDate) {
      await methods.trigger([TaskFormFields.startDate, TaskFormFields.expectedEndDate])
    }
    if (
      field === 'name' &&
      !isHovered &&
      !task?.estimatedEffortInMinutes &&
      data.name &&
      !isViewMode
    ) {
      populateTaskInformation(
        {
          taskName: data.name,
        },
        {
          onSuccess: (data) => {
            setValue('estimatedEffortInMinutes', data.populateTaskInformation.estimatedMinutes)
            setValue('category', data.populateTaskInformation.category)
            clearErrors(['estimatedEffortInMinutes', 'category'])
          },
        }
      )
    }

    // Due date should not auto-update when start date changes per user request

    // change status to TODO when I assignee is changed: Used for accept/decline suggested task
    if (field === TaskFormFields.assigneeId) {
      data.status === UserTaskItemStatus.Archived &&
        setValue(TaskFormFields.status, UserTaskItemStatus.Todo)
      // Only mark as changed if the assignee actually changed from the original value
      isAssigneeChangedForTask = task?.assigneeId !== data.assigneeId
    }

    if (
      planStartDate.current &&
      data.startDate &&
      startDate.isBefore(moment(planStartDate.current))
    ) {
      setIsStartDateBeforePlanStartDate(true)
    } else {
      setIsStartDateBeforePlanStartDate(false)
    }
    if (field === 'estimatedEffortInMinutes') checkHourWarning()
    if (isViewMode) {
      const shouldShowWarning = checkWarningPopup(field)

      // Read fresh errors via methods.formState — the destructured `errors`
      // is captured at render time and won't reflect post-await trigger state.
      if (!shouldShowWarning && isEmpty(methods.formState.errors)) {
        updateTaskData(isAssigneeChangedForTask)
      }
    }
  }

  const checkWarningPopup = (field: string): boolean => {
    if (field === 'startDate' || field === 'expectedEndDate' || field === 'goalId') {
      return checkDateWarning()
    }
    return false
  }

  const checkDateWarning = (): boolean => {
    const data = hookForms.getForm(FORM.USER_TASK_FORM)?.getValues()
    if (!data.planId) return false
    const endDateofGoal = !isEmpty(data.goalExpectedEndDate)
      ? moment(data.goalExpectedEndDate)
      : moment(goalExpectedEndDate)
    const currentTaskEstimatedHours = getHoursCeiling(data.estimatedEffortInMinutes)
    const startDate = moment(data.startDate)
    const expectedEndDate = moment(data.expectedEndDate)

    if (startDate.isAfter(endDateofGoal)) {
      setShowWarningPopup('startDateOutsideGoalTimelinePopup')
      return true
    }

    if (expectedEndDate.isAfter(endDateofGoal)) {
      setGoalExpectedEndDate(expectedEndDate.format('YYYY-MM-DD'))
      setShowWarningPopup('endDateOutsideGoalTimelinePopup')
      return true
    }
    if (
      !isDateInCurrentWeekAndPast(data.startDate) &&
      shouldShowWarningPopup(allTasks ?? [], startDate, endDateofGoal, currentTaskEstimatedHours)
    ) {
      setShowWarningPopup('moreThanCapacityPopup')
      return true
    }
    return false
  }

  const checkHourWarning = (): boolean => {
    const data = hookForms.getForm(FORM.USER_TASK_FORM)?.getValues()
    const currentTaskestimatedHours = getHoursCeiling(data.estimatedEffortInMinutes)
    if (currentTaskestimatedHours > 8) {
      setShowWarningPopup('effortPopup')
      return true
    }
    return false
  }

  const updateTaskData = (isAssigneeChangedForTask?: boolean) => {
    let data = hookForms.getForm(FORM.USER_TASK_FORM)?.getValues()
    let taskData = data
    delete taskData.goalExpectedEndDate
    delete taskData.milestoneId
    delete taskData.isOverDue
    setTaskStartDate(taskData.startDate)
    updateUserTask(
      {
        input: {
          ...taskData,
          taskId: task?.taskId,
          tenantId,
          userId: task?.userId ?? userId,
          meetingId: task?.meetingId ?? meetingId,
          suggestedStatus: isAssigneeChangedForTask
            ? UserTaskSuggestedItemStatus.Pending
            : undefined,
        },
      },
      {
        onSuccess: () => {
          // Invalidate all relevant queries to ensure fresh data
          queryClient.invalidateQueries('getUserTasks')
          queryClient.invalidateQueries('getAdvisorAssignedTasks')
          queryClient.invalidateQueries('getBusinessGoal')
          queryClient.invalidateQueries('getUserTask')

          refetch()
        },
        onSettled: (e) => {
          console.log({ e })
        },
      }
    )
  }

  const handleGoalEndDateUpdate = () => {
    let data = hookForms.getForm(FORM.USER_TASK_FORM)?.getValues()
    updateGoal({
      input: {
        expectedEndDate: goalExpectedEndDate,
        goalId: data.goalId ?? '',
        tenantId,
      },
    })
    isViewMode ? updateTaskData() : handlCreateTask()
    setValue('goalExpectedEndDate', goalExpectedEndDate)
    setShowWarningPopup('none')
  }
  const dispatch = useDispatch()
  useEffect(() => {
    // Wait for both advisorsData and goals data to be available before setting form values
    if (advisorsData.length > 0 && !isGoalsLoading) {
      let initialValues = {
        planId: plan?.id,
        assigneeId: user.id,
        status: UserTaskItemStatus.Todo,
        estimatedEffortInMinutes: 0,
        taskPriority: BusinessGoalPriority.MediumPriority,
        goalId: getDefaultGoalId(),
        goalExpectedEndDate: goalEndDate,
        instructionForTask: initialDescription || '', // Pre-populate with chat context if provided
      }
      const formData = pickBy(
        task
          ? {
              ...task,
              assigneeId: task?.assigneeId ?? task?.userId,
              goalId: task.goalId || GOALIDS.MY_OTHER_TASKS,
              goalExpectedEndDate: goalEndDate,
            }
          : initialValues,
        (value, key) => formKeys[FORM.USER_TASK_FORM].includes(key)
      )
      dispatch(setFormData({ form: [FORM.USER_TASK_FORM], data: formData }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisorsData, isGoalsLoading, hasOtherGoalsAvailable, task?.taskId])

  const handlCreateTask = () => {
    let data = hookForms.getForm(FORM.USER_TASK_FORM)?.getValues()
    data.createdById = user.id
    delete data.goalExpectedEndDate
    delete data.milestoneId
    const taskSource = data.goalId === 'other-tasks' ? UserTaskSource.General : UserTaskSource.Goal
    const userTaskDetails: UserTask = {
      name: data.name,
      instructionForTask: data.instructionForTask,
      category: data.category,
      taskPriority: data.taskPriority,
      assigneeId: data.assigneeId,
      createdById: user.id,
      source: taskSource,
      createdBy: `${user.firstName} ${user.lastName}`,
    }

    setCreatedUserTask && setCreatedUserTask(userTaskDetails)
    createUserTask(
      // { input: { ...data, source: taskSource, tenantId, userId, meetingId } },
      {
        input: {
          ...data,
          source: taskSource,
          tenantId,
          userId: userId || data.assigneeId,
          goalName,
          meetingId,
        },
      },
      {
        onSuccess: () => {
          // Invalidate all relevant queries to ensure fresh data
          queryClient.invalidateQueries('getUserTasks')
          queryClient.invalidateQueries('getAdvisorAssignedTasks')
          queryClient.invalidateQueries('getBusinessGoal')
          queryClient.invalidateQueries('getUserTask')

          refetch()
        },
      }
    )
  }

  const handleTaskSubmission = () => {
    if (isEmpty(errors) && !checkDateWarning() && !isViewMode) {
      handlCreateTask()
    }
  }
  const {
    mutate: searchHowToDoTask,
    isSuccess: isSearchHowToDoTaskSuccess,
    data: searchHowToDoTaskData,
    isLoading: isSearchHowToDoTaskLoading,
    isError: isSearchHowToDoTaskError,
    error: searchHowToDoTaskError,
    reset: resetSearchHowToDoTask,
  } = useSearchHowToDoTaskMutation()

  // Poll the task while tutorial is generating
  const { data: pollingTaskData } = useGetUserTaskQuery(
    {
      input: {
        taskId: task?.taskId || '',
        tenantId,
        userId: userId || task?.userId || '',
      },
    },
    {
      enabled: isTutorialGenerating && !!task?.taskId,
      refetchInterval: (data) => {
        const status = data?.getUserTask?.tutorialGenerationStatus

        // Stop polling if completed or failed
        if (
          status === TutorialGenerationStatus.Completed ||
          status === TutorialGenerationStatus.Failed
        ) {
          return false
        }

        // Safety timeout: Stop polling after 2 minutes
        if (tutorialGenerationStartTime && Date.now() - tutorialGenerationStartTime > 900000) {
          return false
        }

        // Poll every 2 seconds while generating
        return 2000
      },
    }
  )

  useEffect(() => {
    if (isSearchHowToDoTaskSuccess && searchHowToDoTaskData) {
      const response = searchHowToDoTaskData.searchHowToDoTask
      const howToData = {
        howToId: uuid(),
        title: response.text,
        url: JSON.stringify(response.videoLinks),
        steps: response.steps,
      }

      // Handle structured steps if available (enhanced tutorial)
      if (response.structuredSteps && response.structuredSteps.length > 0) {
        setStructuredSteps(
          response.structuredSteps.filter((step) => step !== null) as UserTaskHowToStep[]
        )
      } else {
        setStructuredSteps(undefined)
      }

      if (!response.text && response.videoLinks.length === 0) {
        setTutorialNotFound(true)
      } else {
        setTutorialNotFound(false)
      }

      setValue('howToLinks', [howToData])
      setValue('howToDo', response.text)
      setGeneratedHowToDo(response.text)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchHowToDoTaskSuccess, searchHowToDoTaskData])

  // Watch for tutorial generation status changes from polling
  useEffect(() => {
    if (!isTutorialGenerating) return

    const polledTask = pollingTaskData?.getUserTask
    if (!polledTask?.tutorialGenerationStatus) return

    const status = polledTask.tutorialGenerationStatus

    if (status === TutorialGenerationStatus.Completed) {
      // Success! Update UI
      console.log('Tutorial generation completed successfully!')
      setIsTutorialGenerating(false)
      setTutorialGenerationStartTime(null)

      // First, try to use structuredSteps directly from polled task (enhanced tutorial format)
      if (polledTask.structuredSteps && polledTask.structuredSteps.length > 0) {
        const filteredSteps = polledTask.structuredSteps.filter(
          (step) => step !== null
        ) as UserTaskHowToStep[]
        setStructuredSteps(filteredSteps)
        console.log('Loaded structured steps from polled task', {
          count: filteredSteps.length,
        })
      }

      if (polledTask.howToLinks && polledTask.howToLinks.length > 0) {
        const howToData = polledTask.howToLinks[0]
        if (howToData) {
          setGeneratedHowToDo(howToData.title || '')
          setValue('howToDo', howToData.title || '')
          setValue('howToLinks', polledTask.howToLinks)

          // Fallback: Parse structured steps from howToLinks.steps if structuredSteps not available
          if (
            (!polledTask.structuredSteps || polledTask.structuredSteps.length === 0) &&
            howToData.steps
          ) {
            try {
              const steps = JSON.parse(howToData.steps)
              if (Array.isArray(steps) && steps.length > 0) {
                setStructuredSteps(steps.filter((step) => step !== null) as UserTaskHowToStep[])
                console.log('Loaded structured steps from howToLinks.steps (legacy fallback)', {
                  count: steps.length,
                })
              }
            } catch (e) {
              console.error('Failed to parse tutorial steps', e)
            }
          }

          setTutorialNotFound(false)
        } else {
          setTutorialNotFound(true)
        }
      } else if (polledTask.howToDo) {
        // Has howToDo text but no howToLinks - still a valid tutorial
        setGeneratedHowToDo(polledTask.howToDo)
        setValue('howToDo', polledTask.howToDo)
        setTutorialNotFound(false)
      } else {
        setTutorialNotFound(true)
      }

      // Invalidate all task queries to refresh data everywhere
      reactQueryClient.invalidateQueries(['GetUserTask'])
      reactQueryClient.invalidateQueries(['GetUserTasks'])

      // Also call the parent refetch
      refetch()
    } else if (status === TutorialGenerationStatus.Failed) {
      // Failed - show error
      console.error('Tutorial generation failed')
      setIsTutorialGenerating(false)
      setTutorialGenerationStartTime(null)
      setTutorialNotFound(true)

      if (polledTask.tutorialGenerationError) {
        console.error('Tutorial generation error:', polledTask.tutorialGenerationError)
      }

      // Invalidate queries to refresh UI
      reactQueryClient.invalidateQueries(['GetUserTask'])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pollingTaskData?.getUserTask?.tutorialGenerationStatus,
    pollingTaskData?.getUserTask?.howToLinks,
    isTutorialGenerating,
  ])

  // Safety timeout: Stop generating after 2 minutes
  useEffect(() => {
    if (!isTutorialGenerating || !tutorialGenerationStartTime) return

    const timeout = setTimeout(() => {
      if (isTutorialGenerating) {
        console.error('Tutorial generation timed out on frontend (2 minutes)')
        setIsTutorialGenerating(false)
        setTutorialNotFound(true)
        setTutorialGenerationStartTime(null)
      }
    }, 120000) // 2 minutes

    return () => clearTimeout(timeout)
  }, [isTutorialGenerating, tutorialGenerationStartTime])

  // Handle errors from tutorial generation mutation (AppSync timeout expected)
  useEffect(() => {
    if (isSearchHowToDoTaskError) {
      const error = searchHowToDoTaskError as any

      // Check if it's a timeout error (expected - Lambda continues running)
      if (error?.message?.includes('Execution timed out') || error?.message?.includes('timeout')) {
        console.log('AppSync timeout (expected) - Tutorial generation continues in background')
        // Don't show error, keep polling
      } else {
        // Real error - stop everything
        console.error('Failed to generate tutorial:', searchHowToDoTaskError)
        setIsTutorialGenerating(false)
        setTutorialNotFound(true)
        setTutorialGenerationStartTime(null)
      }

      // Reset mutation state to allow retry
      setTimeout(() => {
        resetSearchHowToDoTask()
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchHowToDoTaskError])

  useEffect(() => {
    if (isCreateTaskSuccess) {
      setShowTaskModal(false)
      setShowTaskSuccessModal && setShowTaskSuccessModal(true)
      refetch()
    }

    if (isCreateTaskError) {
      setShowTaskModal(false)
      setShowTaskErrorModal && setShowTaskErrorModal(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateTaskSuccess, isCreateTaskError])

  // Reset tutorial generation state when viewing a different task
  useEffect(() => {
    if (task?.taskId && task.tutorialGenerationStatus === TutorialGenerationStatus.Generating) {
      // Task is in generating state (possibly from previous session or page reload)
      console.log('Detected task in GENERATING state, starting polling...')
      setIsTutorialGenerating(true)
      setTutorialGenerationStartTime(Date.now())
    } else if (
      task?.taskId &&
      task.tutorialGenerationStatus === TutorialGenerationStatus.Completed
    ) {
      // Task has completed tutorial
      setIsTutorialGenerating(false)
      setTutorialGenerationStartTime(null)

      // Load existing tutorial data
      if (task.howToLinks && task.howToLinks.length > 0) {
        const howToData = task.howToLinks[0]
        if (howToData) {
          setGeneratedHowToDo(howToData.title || '')
          if (howToData.steps) {
            try {
              const steps = JSON.parse(howToData.steps)
              if (Array.isArray(steps)) {
                setStructuredSteps(steps.filter((step) => step !== null) as UserTaskHowToStep[])
              }
            } catch (e) {
              console.error('Failed to parse tutorial steps', e)
            }
          }
        }
      }
    } else {
      // Reset state for tasks without tutorial generation
      setIsTutorialGenerating(false)
      setTutorialGenerationStartTime(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.taskId, task?.tutorialGenerationStatus])

  // Handler to start tutorial generation with polling
  const handleStartTutorialGeneration = () => {
    if (!task?.taskId) return

    // Start polling state
    setIsTutorialGenerating(true)
    setTutorialGenerationStartTime(Date.now())

    // Trigger mutation (AppSync will timeout, but Lambda continues)
    // Pass sourceGoalId to track where the user is viewing from (all-plays, other-tasks, or specific goal)
    searchHowToDoTask({
      input: {
        taskId: task.taskId,
        userId: userId || task.userId || '',
      },
    })
  }

  return {
    isViewMode,
    generatedHowToDo,
    showWarningPopup,
    setShowWarningPopup,
    goalExpectedEndDate,
    setGoalExpectedEndDate,
    planStartDate,
    methods,
    tutorialNotFound,
    clickedFields,
    isCreateTaskLoading,
    isCreateTaskSuccess,
    isCreateTaskError,
    handleClick,
    updateTaskData,
    handleGoalEndDateUpdate,
    handleTaskSubmission,
    handlCreateTask,
    searchHowToDoTask,
    handleStartTutorialGeneration,
    isTutorialGenerating,
    isSearchHowToDoTaskLoading,
    isSearchHowToDoTaskSuccess,
    isSearchHowToDoTaskError,
    resetSearchHowToDoTask,
    structuredSteps,
    setStructuredSteps,
    setGeneratedHowToDo,
    setTutorialNotFound,
    isStartDateBeforePlanStartDate,
    isUpdateTaskSucess,
    updateTaskLoading,
  }
}

export const getHoursCeiling = (timeInMinutes) => {
  return timeInMinutes > 0 ? Math.ceil(timeInMinutes / 60) : 0
}
